import React, { useState, useEffect } from 'react';
import { X, Trash2, ExternalLink, Phone, User, MapPin, Navigation } from 'lucide-react';
import DateTimePicker from './DateTimePicker';
import authService from '../services/authService';
import StatusSelect from './StatusSelect';

const EditAppointmentModal = ({ appointment, onClose, onUpdate, onDelete, onViewJob, onViewCustomer }) => {
  const [formData, setFormData] = useState({
    scheduled_date: '',
    scheduled_time: '',
    estimated_duration: 60,
    appointment_type: 'first_visit',
    status: 'scheduled',
    assigned_technician_id: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Initialize form data from appointment
  useEffect(() => {
    if (appointment) {
      const scheduledDate = new Date(appointment.scheduled_date);
      const dateStr = scheduledDate.toISOString().split('T')[0];
      const roundedMin = Math.round(scheduledDate.getMinutes() / 5) * 5;
      const timeStr = roundedMin === 60
        ? `${String(scheduledDate.getHours() + 1).padStart(2, '0')}:00`
        : `${String(scheduledDate.getHours()).padStart(2, '0')}:${String(roundedMin).padStart(2, '0')}`;

      setFormData({
        scheduled_date: dateStr,
        scheduled_time: timeStr,
        estimated_duration: appointment.estimated_duration || 60,
        appointment_type: appointment.appointment_type || 'first_visit',
        status: appointment.status || 'scheduled',
        assigned_technician_id: appointment.assigned_technician_id || '',
        notes: appointment.notes || ''
      });
    }
  }, [appointment]);

  // Status options
  const statusOptions = [
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  // Appointment types
  const appointmentTypes = [
    { value: 'first_visit', label: 'First Visit' },
    { value: 'follow_up', label: 'Follow-up' },
    { value: 'inspection', label: 'Inspection' },
    { value: 'treatment', label: 'Treatment' },
    { value: 'emergency', label: 'Emergency' },
    { value: 'final_visit', label: 'Final Visit' }
  ];

  // Duration options (5-minute increments)
  const durationOptions = [];
  for (let m = 15; m <= 480; m += 5) {
    const hours = Math.floor(m / 60);
    const mins = m % 60;
    let label;
    if (m < 60) {
      label = `${m} Mins`;
    } else if (mins === 0) {
      label = hours === 1 ? '1 Hour' : `${hours} Hours`;
    } else {
      label = `${hours === 1 ? '1 Hour' : `${hours} Hours`} ${mins} Mins`;
    }
    durationOptions.push({ value: m, label });
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.scheduled_date) {
      newErrors.scheduled_date = 'Date is required';
    }

    if (!formData.scheduled_time) {
      newErrors.scheduled_time = 'Time is required';
    }

    if (!formData.estimated_duration || formData.estimated_duration < 15) {
      newErrors.estimated_duration = 'Duration must be at least 15 minutes';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      // Combine date and time into ISO format
      const scheduledDateTime = `${formData.scheduled_date}T${formData.scheduled_time}:00`;

      // Calculate scheduled_end_date based on duration
      const scheduledDate = new Date(scheduledDateTime);
      const duration = parseInt(formData.estimated_duration);
      const scheduledEndDate = new Date(scheduledDate.getTime() + (duration * 60000));
      const scheduledEndDateTime = scheduledEndDate.toISOString().replace('T', ' ').replace('Z', '').substring(0, 19);

      const updateData = {
        scheduled_date: scheduledDateTime,
        scheduled_end_date: scheduledEndDateTime,
        estimated_duration: duration,
        appointment_type: formData.appointment_type,
        status: formData.status,
        assigned_technician_id: formData.assigned_technician_id || null,
        notes: formData.notes
      };

      onUpdate(updateData);
    }
  };

  const handleDelete = () => {
    onDelete(appointment.id);
    onClose();
  };

  if (!appointment) return null;

  // Build address string
  const address = [
    appointment.treatment_address_line1,
    appointment.treatment_address_line2,
    appointment.treatment_address_city,
    appointment.treatment_address_postcode
  ].filter(Boolean).join(', ');

  // Treatment contact - prefer treatment contact, fall back to customer
  const contactName = appointment.treatment_contact_name || appointment.customer_name;
  const contactPhone = appointment.treatment_contact_phone || appointment.customer_phone;

  // Directions URL - Apple Maps on iOS/Mac, Google Maps elsewhere
  const isApple = /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent);
  const directionsUrl = address
    ? isApple
      ? `https://maps.apple.com/?daddr=${encodeURIComponent(address)}`
      : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`
    : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-5 py-3 border-b border-gray-200 bg-gradient-to-r from-pest-blue to-pest-teal">
          <div>
            <h2 className="text-lg font-bold text-white leading-tight">{appointment.job_title || 'Edit Appointment'}</h2>
            <p className="text-white/80 text-xs mt-0.5">
              {appointment.customer_name ? `Billing Customer: ${appointment.customer_name}` : ''}{address ? ` \u2022 ${appointment.treatment_address_postcode || ''}` : ''}
              {appointment.technician_name ? ` \u2022 ${appointment.technician_name}` : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors ml-3"
          >
            <X size={20} />
          </button>
        </div>

        {/* Treatment Address, Contact & View Job */}
        <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 space-y-2">
          {/* Treatment Address - clickable for navigation */}
          {address && (
            <div className="flex items-center gap-3">
              <div className="flex items-start gap-2 text-sm text-gray-700 flex-1 min-w-0">
                <MapPin size={14} className="text-pest-blue mt-0.5 flex-shrink-0" />
                <span className="font-medium">{address}</span>
              </div>
              <a
                href={directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 text-sm font-medium text-white bg-pest-blue hover:bg-pest-teal rounded-lg transition-colors flex items-center gap-1.5 flex-shrink-0"
              >
                <Navigation size={13} />
                Directions
              </a>
            </div>
          )}

          {/* Treatment Contact */}
          {(contactName || contactPhone) && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              {contactName && (
                <span className="text-sm text-gray-700 flex items-center gap-1.5">
                  <User size={14} className="text-gray-400" />
                  {contactName}
                </span>
              )}
              {contactPhone && (
                <a href={`tel:${contactPhone}`} className="text-sm text-gray-600 hover:text-pest-blue flex items-center gap-1.5 transition-colors">
                  <Phone size={14} className="text-gray-400" />
                  {contactPhone}
                </a>
              )}
            </div>
          )}

          {/* View Job button */}
          {appointment.job_id && onViewJob && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => { onViewJob(appointment); onClose(); }}
                className="px-3 py-1.5 text-sm font-medium text-white bg-pest-blue hover:bg-pest-teal rounded-lg transition-colors flex items-center gap-1.5"
              >
                <ExternalLink size={13} />
                View Job
              </button>
            </div>
          )}
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Date & Time */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date & Time</label>
              <DateTimePicker
                value={formData.scheduled_date && formData.scheduled_time ? `${formData.scheduled_date}T${formData.scheduled_time}` : formData.scheduled_date || ''}
                onChange={(val) => {
                  if (!val) {
                    handleChange({ target: { name: 'scheduled_date', value: '' } });
                    handleChange({ target: { name: 'scheduled_time', value: '' } });
                  } else {
                    const [d, t] = val.split('T');
                    handleChange({ target: { name: 'scheduled_date', value: d || '' } });
                    handleChange({ target: { name: 'scheduled_time', value: t || '' } });
                  }
                }}
                className="w-full"
              />
              {(errors.scheduled_date || errors.scheduled_time) && (
                <p className="text-red-500 text-xs mt-1">{errors.scheduled_date || errors.scheduled_time}</p>
              )}
            </div>

            {/* Duration & Type row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="estimated_duration" className="block text-xs font-medium text-gray-600 mb-1">Duration</label>
                <select
                  id="estimated_duration"
                  name="estimated_duration"
                  value={formData.estimated_duration}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border-2 rounded-lg text-sm focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors cursor-pointer ${
                    errors.estimated_duration ? 'border-red-500' : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  {durationOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {errors.estimated_duration && <p className="text-red-500 text-xs mt-1">{errors.estimated_duration}</p>}
              </div>

              <div>
                <label htmlFor="appointment_type" className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                <select
                  id="appointment_type"
                  name="appointment_type"
                  value={formData.appointment_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pest-blue focus:border-pest-blue hover:border-gray-400 transition-colors cursor-pointer"
                >
                  {appointmentTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label htmlFor="notes" className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pest-blue focus:border-pest-blue hover:border-gray-400 transition-colors resize-none"
                placeholder="Appointment notes..."
              />
            </div>

          </form>
        </div>

        {/* Footer - outside scroll area so dropdown extends upward */}
        <div className="px-5 py-4 border-t border-gray-200 flex items-end justify-between overflow-visible">
          <div className="flex items-end gap-3">
            <div>
              <span className="block text-sm font-semibold text-gray-700 mb-1.5">Status</span>
              <StatusSelect
                value={formData.status}
                onChange={(val) => handleChange({ target: { name: 'status', value: val } })}
                options={statusOptions}
                size="lg"
                dropUp
              />
            </div>
            {(authService.isAdmin() || authService.hasAdminReturn()) && (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="text-red-500 hover:text-red-700 text-sm font-medium flex items-center gap-1 transition-colors mb-1"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={handleSubmit}
            className="btn btn-lg btn-primary"
          >
            Save
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 pt-8 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-3">Confirm Deletion</h3>
            <p className="text-gray-600 text-sm mb-5">
              Are you sure you want to delete this appointment? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditAppointmentModal;
